import React, { useContext, useEffect, useMemo, useReducer } from 'react';

import cx from 'classnames';
import { Icon } from 'design-system-web';
import { MediaPickerButton, Tooltip } from 'components';
import { kebabCase } from 'lodash';
import { useTranslation } from 'react-i18next';

import { UserDataContext, AnalyticsContext } from 'lane-shared/contexts';
import {
  getLibraryOptions,
  getValidationMessages,
  toSchema,
} from 'lane-shared/helpers';
import { ANALYTIC_KEYS } from 'constants-analytics';
import { ActiveChannelTypeEnum, CHANNEL_DATA_TAGS } from 'constants-channel';
import { imageUrl } from 'lane-shared/helpers/formatters';
import { useFlag, useMediaQuery } from 'lane-shared/hooks';
import useFormattedNumberInput from 'lane-shared/hooks/useFormattedNumberInput';

import { FeatureFlag } from 'constants-flags';
import type {
  AddressType,
  Channel,
  ContentType,
  ProfileType,
} from 'application-types';

import { Dropdown, Input, Toggle } from 'components/form';
import Label from 'components/general/Label';
import ShowId from 'components/lane/ShowId';
import CardContainer from 'components/cards/CardContainer';
import { H4, H5 } from 'components/typography';

import AddressEditInline from '../AddressEditInline';
import ChannelInfoEditMultifamily from './ChannelInfoEditMultifamily';
import {
  SuitesMultiSelectDropdown,
  SuitesFields,
} from '../SuitesMultiSelectDropdown';
import { PropertyCesSyncedStatus } from './PropertyCesSyncedStatus';

import styles from './ChannelInfoEdit.scss';

type ChannelWithProperty = Channel & {
  property?: {
    id: string;
  } | null;
};

type Props = {
  onChannelUpdated: (channel: Partial<ChannelWithProperty>) => void;
  channel: ChannelWithProperty;
  validation: any;
  className?: string;
  style?: React.CSSProperties;
  testId?: undefined;
  heading?: string | React.ReactNode;
  shouldShowStats?: boolean;
  shouldShowType?: boolean;
  isInfoPage?: boolean;
  channelForDataIdentifiers: Channel;
  forCreate?: boolean;
  renderSuitSelector?: boolean;
  onSuitesUpdate?: (data: SuitesFields) => void;
};

enum AutofilledField {
  uniqueLink = 'slug',
  displayName = 'name',
}

export default function ChannelInfoEdit({
  onChannelUpdated,
  channel,
  validation = null,
  className,
  style,
  heading,
  shouldShowStats = false,
  shouldShowType = false,
  isInfoPage = false,
  channelForDataIdentifiers,
  forCreate = false,
  renderSuitSelector = false,
  onSuitesUpdate,
}: Props) {
  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);
  const analytics = useContext(AnalyticsContext);

  function updateChannel({
    fields = {},
    profileFields,
  }: {
    fields?: Partial<ChannelWithProperty>;
    profileFields?: Partial<ProfileType>;
  }) {
    const updatedProfile = {
      ...(channel?.profile || {}),
      ...(profileFields || {}),
    } as ProfileType;

    if (profileFields && updatedProfile.content?._id) {
      // There maybe full content here loaded from the server.
      // but we don't want to set full content info back.
      // Just the id.  ProfileInput expects a ContentIdInput.
      updatedProfile.content = {
        // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
        _id: channel.profile?.content._id,
      } as Partial<ContentType> as ContentType;
    }

    onChannelUpdated({
      ...fields,
      ...(profileFields ? { profile: updatedProfile } : {}),
    });
  }

  const isCreateAction = Boolean(!channel.__typename);

  function reducer(
    dirtyFields: { [key in AutofilledField]?: boolean },
    action: { field: AutofilledField }
  ) {
    return {
      ...dirtyFields,
      [action.field]: true,
    };
  }

  const [dirtyFields, dispatchFieldModified] = useReducer(reducer, {
    [AutofilledField.displayName]: false,
    [AutofilledField.uniqueLink]: false,
  });

  const shouldAutofill =
    isCreateAction &&
    (!dirtyFields[AutofilledField.displayName] ||
      !dirtyFields[AutofilledField.uniqueLink]);

  function setTag(tag: string, value: boolean): void {
    const tags = new Set(channelForDataIdentifiers.tags);

    if (value) {
      tags.add(tag);
    } else {
      tags.delete(tag);
    }

    updateChannel({ fields: { tags: Array.from(tags) } });
  }

  function getTag(tagName: string): boolean {
    return channelForDataIdentifiers.tags.includes(tagName);
  }

  const channelTypeOptions = useMemo(
    () => Object.values(ActiveChannelTypeEnum).map(toSchema),
    []
  );

  const channelTypeErrors =
    getValidationMessages(validation, 'type') || undefined;

  const childTypeName =
    channel.parent &&
    (channel.type === ActiveChannelTypeEnum.Property
      ? ActiveChannelTypeEnum.Property
      : 'Location');

  const {
    maskedValue: headcountDisplayValue,
    inputOnChange: onChangeHeadcount,
    inputOnBlur: onBlurHeadcount,
  } = useFormattedNumberInput({
    min: 0,
    max: Number.MAX_SAFE_INTEGER,
    value: channel.stats?.subscribers,
    allowEmptyInput: isCreateAction,
    maximumFractionDigits: 0,
    onValueChange: subscribers =>
      updateChannel({
        fields: {
          stats: {
            ...channel.stats,
            subscribers,
          },
        },
      }),
  });
  const shouldRenderPhysicalBuildingTag = useMemo(() => {
    // only render for child channels or sub channels
    return (
      channelForDataIdentifiers.type === ActiveChannelTypeEnum.Property &&
      channelForDataIdentifiers.parent
    );
  }, [channelForDataIdentifiers.type, channelForDataIdentifiers.parent]);

  const {
    maskedValue: squareFootageDisplayValue,
    inputOnChange: onChangeSquareFootage,
    inputOnBlur: onBlurSquareFootage,
  } = useFormattedNumberInput({
    min: 0,
    max: Number.MAX_SAFE_INTEGER,
    value: channel.stats?.sf,
    allowEmptyInput: isCreateAction,
    maximumFractionDigits: 0,
    onValueChange: sf =>
      updateChannel({
        fields: { stats: { ...channel.stats, sf } },
      }),
  });

  function autofillFields() {
    const slugUpdate = dirtyFields[AutofilledField.uniqueLink]
      ? {}
      : { slug: kebabCase(channel?.name) };
    const displayNameUpdate = dirtyFields[AutofilledField.displayName]
      ? {}
      : { name: channel?.name };

    updateChannel({
      fields: {
        name: channel?.name,
        ...slugUpdate,
      },
      profileFields: displayNameUpdate,
    });
  }

  useEffect(() => {
    if (shouldAutofill) {
      autofillFields();
    }
  }, [channel?.name, shouldAutofill, dirtyFields]);

  // isCustomer is a new field with no default value, let's check if it has been set already
  const isChannelIsCustomerDefined = !(
    typeof channel.isCustomer === 'undefined' || channel.isCustomer === null
  );

  const isMultifamilyFlagEnabled = useFlag(
    FeatureFlag.MultifamilyCreateProperty,
    false
  );
  const tenantManagementEnabled = useFlag(FeatureFlag.TenantManagement, false);
  const isVisitorsTenantCustomEmailBannerEnabled = useFlag(
    FeatureFlag.VisitorsTenantCustomEmailBanner,
    false
  );
  const isTenantChannel = Boolean(channel?.parent);
  const shouldShowEmailBanner =
    isVisitorsTenantCustomEmailBannerEnabled && isTenantChannel;
  const visitorEmailBannerMedia = useMediaQuery({
    mediaId: shouldShowEmailBanner
      ? channel.profile?.visitorEmailBannerId
      : undefined,
  });

  const renderEmailBannerCardContent = () => {
    if (
      channel.profile?.visitorEmailBannerId &&
      visitorEmailBannerMedia?.media
    ) {
      return (
        <>
          <div
            className={styles.emailBannerPreview}
            style={{
              backgroundImage: `url(${imageUrl(channel.profile.visitorEmailBannerId)})`,
            }}
            role="img"
            aria-label={t(
              'web.admin.channel.profile.info.emailBanner'
            )}
            data-test="channel-info-email-banner-preview"
          />
          <H5 className={styles.emailBannerText}>
            {visitorEmailBannerMedia.media.name}
          </H5>
        </>
      );
    }
    if (
      channel.profile?.visitorEmailBannerId &&
      visitorEmailBannerMedia?.error
    ) {
      return (
        <H5
          className={styles.emailBannerText}
          data-test="channel-info-email-banner-failed-message"
        >
          {t(
            'web.admin.channel.profile.info.emailBanner.failedToLoad'
          )}
        </H5>
      );
    }
    if (
      channel.profile?.visitorEmailBannerId &&
      visitorEmailBannerMedia?.loading
    ) {
      return (
        <H5
          className={styles.emailBannerText}
          data-test="channel-info-email-banner-loading"
        >
          {t('web.admin.channel.profile.info.emailBanner.loading')}
        </H5>
      );
    }
    return (
      <H5
        className={styles.emailBannerText}
        data-test="channel-info-email-banner-contact-message"
      >
        {t(
          'web.admin.channel.profile.info.emailBanner.notFound'
        )}
      </H5>
    );
  };

  if (isMultifamilyFlagEnabled) {
    return (
      <ChannelInfoEditMultifamily
        {...{
          onChannelUpdated,
          channel,
          validation,
          className,
          style,
          heading,
          shouldShowStats,
          shouldShowType,
          isInfoPage,
          channelForDataIdentifiers,
          forCreate,
          onSuitesUpdate,
          renderSuitSelector: renderSuitSelector && tenantManagementEnabled,
        }}
      />
    );
  }

  return (
    <section className={cx(styles.ChannelInfoEdit, className)} style={style}>
      {heading && <H4>{heading}</H4>}
      {shouldShowType && (
        <Dropdown<ActiveChannelTypeEnum>
          isFullWidth
          testId="channelTypeDropdown"
          placeholder={t(
            'web.admin.channel.profile.info.channelType.placeHolder'
          )}
          invalid={Boolean(channelTypeErrors)}
          errors={channelTypeErrors}
          items={channelTypeOptions}
          value={channel.type}
          onValueChange={type => updateChannel({ fields: { type } })}
        />
      )}
      <Input
        fieldName="name"
        label={t('web.admin.channel.profile.info.internalName')}
        testId="channelNameInput"
        value={channel.name || ''}
        error={getValidationMessages(validation, 'name') || undefined}
        onChange={name => updateChannel({ fields: { name } })}
        placeholder={t(
          'web.admin.channel.profile.info.internalName.placeHolder'
        )}
      />
      <Input
        fieldName="displayName"
        value={channel.profile?.name || ''}
        label={t('web.admin.channel.profile.info.displayName')}
        testId="profileDisplayName"
        placeholder={t(
          'web.admin.channel.profile.info.displayName.placeHolder'
        )}
        onChange={(name: string) => {
          if (!dirtyFields[AutofilledField.displayName]) {
            dispatchFieldModified({ field: AutofilledField.displayName });
          }

          updateChannel({ profileFields: { name } });
        }}
        error={getValidationMessages(validation, 'profile.name') || undefined}
      />
      <Input
        fieldName="uniqueLink"
        value={channel.slug}
        label={t('web.admin.channel.profile.info.uniqueLink')}
        testId="channelUniqueLinkInput"
        onChange={(slug: string) => {
          if (!dirtyFields[AutofilledField.uniqueLink]) {
            dispatchFieldModified({ field: AutofilledField.uniqueLink });
          }

          updateChannel({ fields: { slug } });
        }}
        error={getValidationMessages(validation, 'slug') || undefined}
        placeholder={t('web.admin.channel.profile.info.uniqueLink.placeHolder')}
      />

      {shouldShowStats && (
        <>
          <Input
            label={
              childTypeName
                ? t('web.admin.channel.profile.info.channelHeadCount', {
                    childTypeName,
                  })
                : t('web.admin.channel.profile.info.channelHeadCountDefault')
            }
            error={getValidationMessages(validation, 'stats.subscribers')}
            placeholder={t(
              'web.admin.channel.profile.info.channelHeadCount.placeHolder'
            )}
            testId="headcount"
            value={headcountDisplayValue}
            onChange={onChangeHeadcount}
            onBlur={onBlurHeadcount}
          />

          <Input
            label={
              childTypeName
                ? t('web.admin.channel.profile.info.channelSquareFootage', {
                    childTypeName,
                  })
                : t(
                    'web.admin.channel.profile.info.channelSquareFootageDefault'
                  )
            }
            error={getValidationMessages(validation, 'stats.sf')}
            placeholder={t(
              'web.admin.channel.profile.info.channelSquareFootage.placeHolder'
            )}
            testId="squareFootage"
            value={squareFootageDisplayValue}
            onChange={onChangeSquareFootage}
            onBlur={onBlurSquareFootage}
          />
        </>
      )}

      <Input
        fieldName="description"
        value={channel.description || ''}
        label={t('web.admin.channel.profile.info.description')}
        testId="channelDescriptionInput"
        onChange={(description: string) =>
          updateChannel({ fields: { description } })
        }
        error={getValidationMessages(validation, 'description') || undefined}
        placeholder={t(
          'web.admin.channel.profile.info.description.placeHolder'
        )}
        maxLength={200}
        showLengthIndicator
      />
      <div className={styles.address}>
        {!isCreateAction && (
          <Label mt={0} className={styles.label}>
            {t('web.admin.channel.profile.info.address')}
          </Label>
        )}
        <AddressEditInline
          address={channel.address || ({} as AddressType)}
          onAddressUpdated={(address: AddressType) =>
            onChannelUpdated({ address })
          }
          isNewAddress={isCreateAction}
        />
      </div>

      <Input
        type="email"
        label={t('web.admin.channel.profile.info.supportEmail')}
        placeholder={t(
          'web.admin.channel.profile.info.supportEmail.placeHolder'
        )}
        value={channel.profile?.email || ''}
        onChange={email => updateChannel({ profileFields: { email } })}
        error={getValidationMessages(validation, 'profile.email') || undefined}
        testId="profileDisplayEmail"
      />
      {tenantManagementEnabled && renderSuitSelector && (
        <SuitesMultiSelectDropdown onSuitesUpdate={onSuitesUpdate} />
      )}
      <div>
        <H4 mb={4}>{t('web.admin.channel.profile.info.logo')}</H4>
        <MediaPickerButton
          libraries={getLibraryOptions({
            channel: forCreate && channel.parent ? channel.parent : channel,
          })}
          errors={
            getValidationMessages(validation, 'profile.logo') || undefined
          }
          media={{ _id: channel.profile?.logo || '' }}
          storageKey={channel?._id}
          onMediaSelected={media => {
            updateChannel({ profileFields: { logo: media?._id } });
            analytics.track(
              ANALYTIC_KEYS.ANALYTIC_CHANNEL_PROFILE_LOGO_CHANGE_ATTEMPTED,
              {
                attemptedChannelId: channel?._id,
                attemptedChannelName: channel?.name,
              }
            );
          }}
          className={styles.imageButton}
          testId="profileLogoMediaPicker"
        />

        <div className={styles.imageWrapper}>
          <div
            className={cx(styles.image, styles.logo)}
            style={{
              backgroundImage: `url(${imageUrl(channel.profile?.logo)})`,
            }}
          />
        </div>
      </div>
      {shouldShowEmailBanner && (
        <div
          className={styles.emailBannerSection}
          data-test="channel-info-email-banner-section"
        >
          <H4 mb={4}>{t('web.admin.channel.profile.info.emailBanner')}</H4>
          <div
            className={styles.emailBannerDisplay}
            role="region"
            aria-label={t('web.admin.channel.profile.info.emailBanner')}
          >
            <CardContainer size="small" className={styles.emailBannerCard}>
              {renderEmailBannerCardContent()}
              <Tooltip
                TooltipComponent={
                  <p>
                    {t(
                      'web.admin.channel.profile.info.emailBanner.contactAdminToModify'
                    )}
                  </p>
                }
                placement="right"
              >
                <span
                  className={styles.emailBannerInfoIcon}
                  data-test="channel-info-email-banner-tooltip-icon"
                  aria-label={t(
                    'web.admin.channel.profile.info.emailBanner.contactAdminToModify'
                  )}
                >
                  <Icon name="info-circle" set="FontAwesome" type="far" />
                </span>
              </Tooltip>
            </CardContainer>
          </div>
        </div>
      )}
      {channel.type === ActiveChannelTypeEnum.Property &&
        'property' in channel &&
        channel.parent !== null && (
          <PropertyCesSyncedStatus channel={channel} />
        )}
      {!isInfoPage && (
        <div>
          <H4 mb={4}>{t('web.admin.channel.profile.info.dataIdentifiers')}</H4>
          <div className={styles.dataIdentifiersWrapper}>
            <div>
              <Toggle
                value={getTag(CHANNEL_DATA_TAGS.demo)}
                onChange={value => setTag(CHANNEL_DATA_TAGS.demo, value)}
                text={t(
                  'web.admin.channel.profile.info.dataIdentifiers.demo.text'
                )}
                description={t(
                  'web.admin.channel.profile.info.dataIdentifiers.demo.description'
                )}
                testId="demoChannelToggle"
              />
            </div>
            <div>
              <Toggle
                value={getTag(CHANNEL_DATA_TAGS.contentDistribution)}
                onChange={value =>
                  setTag(CHANNEL_DATA_TAGS.contentDistribution, value)
                }
                text={t(
                  'web.admin.channel.profile.info.dataIdentifiers.contentDistribution.text'
                )}
                description={t(
                  'web.admin.channel.profile.info.dataIdentifiers.contentDistribution.description'
                )}
                testId="contentDistributionChannelToggle"
              />
            </div>
            <div>
              <Toggle
                value={getTag(CHANNEL_DATA_TAGS.flexCompany)}
                onChange={value => setTag(CHANNEL_DATA_TAGS.flexCompany, value)}
                text={t(
                  'web.admin.channel.profile.info.dataIdentifiers.flexCompany.text'
                )}
                description={t(
                  'web.admin.channel.profile.info.dataIdentifiers.flexCompany.description'
                )}
                testId="flexCompanyChannelToggle"
              />
            </div>
            {shouldRenderPhysicalBuildingTag && (
              <div>
                <Toggle
                  value={getTag(CHANNEL_DATA_TAGS.physicalBuilding)}
                  onChange={value =>
                    setTag(CHANNEL_DATA_TAGS.physicalBuilding, value)
                  }
                  text={t(
                    'web.admin.channel.profile.info.dataIdentifiers.physicalBuilding.text'
                  )}
                  description={t(
                    'web.admin.channel.profile.info.dataIdentifiers.physicalBuilding.description'
                  )}
                  testId="physicalPropertyChannelToggle"
                />
              </div>
            )}
            <div>
              <Toggle
                value={isChannelIsCustomerDefined ? channel.isCustomer : false}
                onChange={() => {
                  updateChannel({
                    fields: { isCustomer: !channel.isCustomer },
                  });
                }}
                disabled={
                  Boolean(channel.parent) || Boolean(!user?.isSuperUser)
                }
                text={t('web.admin.channel.profile.info.isCustomer.text')}
                description={t(
                  'web.admin.channel.profile.info.isCustomer.description'
                )}
                testId="isCustomerChannelToggle"
              />
            </div>
          </div>
        </div>
      )}
      <ShowId
        id={channel._id}
        className={styles.channelId}
        testId="channelId"
      />
    </section>
  );
}
